import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  IconButton,
  Typography,
  InputBase,
  styled,
  alpha,
  Grid,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  X,
  Search,
  Settings
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { ReactComponent as BrainIcon } from '../../../assets/images/loading-white.svg';
import { ReactComponent as BrainStoreIcon } from '../../../assets/images/brain.svg';

// Gradientes específicos para cada AGT (desde AGTPromo)
// Light mode (originales)
const CHAT_GRADIENT_LIGHT = 'linear-gradient(to bottom, #415B7E, #2D476A, #193356)';
const EDU_GRADIENT_LIGHT = 'linear-gradient(to bottom, #25505A, #113C46)';
const UPS_GRADIENT_LIGHT = 'linear-gradient(to bottom, #DC9844, #C88430, #B4701C)';
const JOB_GRADIENT_LIGHT = 'linear-gradient(to bottom, #4A4A4A, #333333, #1A1A1A)';
const FIT_GRADIENT_LIGHT = 'linear-gradient(to bottom, #FF8C00, #FF6B00, #FF5722)';
const HEALTH_GRADIENT_LIGHT = 'linear-gradient(to bottom, #4CAF50, #3d8b40, #2e7031)';
const LEGAL_GRADIENT_LIGHT = 'linear-gradient(to bottom, #2C5282, #234876, #1A365D)';
const TRANSLATE_GRADIENT_LIGHT = 'linear-gradient(to right, #3498DB, #5D6FBD, #9B59B6)';
const LIFEOS_GRADIENT_LIGHT = 'linear-gradient(120deg, #415B7E, #2D476A, #4A4F81, #363B6D, #A83836, #BC4C4A, #E78460, #DC9844)';

// Dark mode (versiones más oscuras - reducidos ~40%)
const CHAT_GRADIENT_DARK = 'linear-gradient(to bottom, #2A3D54, #1E2F45, #0F1A2B)';
const EDU_GRADIENT_DARK = 'linear-gradient(to bottom, #16303A, #0A2228)';
const UPS_GRADIENT_DARK = 'linear-gradient(to bottom, #8A5F2A, #7A501D, #6B4312)';
const JOB_GRADIENT_DARK = 'linear-gradient(to bottom, #2D2D2D, #1F1F1F, #0F0F0F)';
const FIT_GRADIENT_DARK = 'linear-gradient(to bottom, #994F00, #994000, #993314)';
const HEALTH_GRADIENT_DARK = 'linear-gradient(to bottom, #2D6930, #245326, #1B421D)';
const LEGAL_GRADIENT_DARK = 'linear-gradient(to bottom, #1B3150, #152B46, #101F38)';
const TRANSLATE_GRADIENT_DARK = 'linear-gradient(to right, #1F5A84, #38426F, #5C356C)';
const LIFEOS_GRADIENT_DARK = 'linear-gradient(120deg, #415B7E, #2D476A, #4A4F81, #363B6D, #A83836, #BC4C4A, #E78460, #DC9844)';

// Componente específico para el icono de Store con gradiente LifeOS
const BrainStoreIconWithGradient = () => {
  const theme = useTheme();
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const isDarkMode = theme.palette.mode === 'dark';
  const gradientId = 'brain-gradient-store-lifeos';

  // Colores del gradiente de LifeOS
  const colors = ['#415B7E', '#2D476A', '#4A4F81', '#363B6D', '#A83836', '#BC4C4A', '#E78460', '#DC9844'];

  return (
    <>
      <svg width="0" height="0" style={{ position: 'absolute', pointerEvents: 'none' }}>
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
            {colors.map((color, index) => (
              <stop
                key={index}
                offset={`${(index / (colors.length - 1)) * 100}%`}
                stopColor={color}
              />
            ))}
          </linearGradient>
        </defs>
      </svg>
      <Box
        component="span"
        sx={{
          display: 'contents',
          '& svg path': {
            fill: `url(#${gradientId})`,
            stroke: `url(#${gradientId})`,
            strokeWidth: '1.5',
          }
        }}
      >
        <BrainStoreIcon />
      </Box>
    </>
  );
};

// Componente wrapper para BrainIcon con gradiente
// En light/dark mode: SVG con gradiente (excepto Chat en dark que usa color sólido más claro)
// En wallpaper mode: Fit usa gradiente, las demás apps usan el color más oscuro sólido
const BrainIconWithGradient = ({ gradient, gradientDark, appId }) => {
  const theme = useTheme();
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const isDarkMode = theme.palette.mode === 'dark';
  const gradientId = `brain-gradient-${appId}`;

  if (!gradient) {
    // Sin gradiente definido: cerebro blanco
    return <BrainIcon />;
  }

  // Extraer colores del gradiente
  const extractColors = (gradientString) => {
    const colorRegex = /#[0-9A-Fa-f]{6}/g;
    return gradientString.match(colorRegex) || [];
  };

  // Función para invertir color (negativo suavizado - gris en lugar de blanco)
  const invertColor = (hex) => {
    // Remover # si existe
    hex = hex.replace('#', '');

    // Convertir a RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Invertir cada componente pero con un máximo de 180 en lugar de 255
    // Esto produce grises suaves en lugar de blancos brillantes
    const maxValue = 180;
    const invR = Math.min(255 - r, maxValue).toString(16).padStart(2, '0');
    const invG = Math.min(255 - g, maxValue).toString(16).padStart(2, '0');
    const invB = Math.min(255 - b, maxValue).toString(16).padStart(2, '0');

    return `#${invR}${invG}${invB}`;
  };

  // Seleccionar gradiente según modo
  const activeGradient = (isDarkMode && gradientDark) ? gradientDark : gradient;
  const colors = extractColors(activeGradient);

  // En wallpaper mode
  if (isWallpaperMode) {
    // Caso especial: Fit mantiene el gradiente en wallpaper mode
    if (appId === 'fit') {
      return (
        <>
          <svg width="0" height="0" style={{ position: 'absolute', pointerEvents: 'none' }}>
            <defs>
              <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
                {colors.map((color, index) => (
                  <stop
                    key={index}
                    offset={`${(index / (colors.length - 1)) * 100}%`}
                    stopColor={color}
                  />
                ))}
              </linearGradient>
            </defs>
          </svg>
          <Box
            component="span"
            sx={{
              display: 'contents',
              '& svg path': {
                fill: `url(#${gradientId})`,
                stroke: `url(#${gradientId})`,
              }
            }}
          >
            <BrainIcon />
          </Box>
        </>
      );
    }

    // Caso especial: Fin usa blanco en wallpaper para mejor visibilidad
    if (appId === 'fin') {
      return (
        <Box
          component="span"
          sx={{
            display: 'contents',
            '& svg path': {
              fill: '#FFFFFF',
              stroke: '#FFFFFF',
            }
          }}
        >
          <BrainIcon />
        </Box>
      );
    }

    // Para las demás apps en wallpaper: color sólido más oscuro
    const darkestColor = colors[colors.length - 1] || '#1A1A1A';

    return (
      <Box
        component="span"
        sx={{
          display: 'contents',
          '& svg path': {
            fill: darkestColor,
            stroke: darkestColor,
          }
        }}
      >
        <BrainIcon />
      </Box>
    );
  }

  // Caso especial: Chat en dark mode usa color sólido más claro
  if (appId === 'chat' && isDarkMode) {
    const lightestColor = colors[0] || '#415B7E';
    return (
      <Box
        component="span"
        sx={{
          display: 'contents',
          '& svg path': {
            fill: lightestColor,
            stroke: lightestColor,
          }
        }}
      >
        <BrainIcon />
      </Box>
    );
  }

  // Caso especial: Fin usa colores negativos en ambos modos para diferenciarse
  const shouldInvertColors = appId === 'fin';
  const finalColors = shouldInvertColors ? colors.map(invertColor) : colors;

  // En light/dark mode: SVG con gradiente
  return (
    <>
      <svg width="0" height="0" style={{ position: 'absolute', pointerEvents: 'none' }}>
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
            {finalColors.map((color, index) => (
              <stop
                key={index}
                offset={`${(index / (finalColors.length - 1)) * 100}%`}
                stopColor={color}
              />
            ))}
          </linearGradient>
        </defs>
      </svg>
      <Box
        component="span"
        sx={{
          display: 'contents',
          '& svg path': {
            fill: `url(#${gradientId})`,
            stroke: `url(#${gradientId})`,
          }
        }}
      >
        <BrainIcon />
      </Box>
    </>
  );
};

// Contenedor principal con blur y animaciones
const LaunchpadContainer = styled(motion.div)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  zIndex: 1400,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '5vh 16px 16px 16px',
  overflowY: 'auto',
  WebkitOverflowScrolling: 'touch',
  msOverflowStyle: '-ms-autohiding-scrollbar',
  [theme.breakpoints.down('sm')]: {
    padding: '3vh 12px 12px 12px',
  }
}));

// Contenedor interno scrollable
const ScrollableContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  minHeight: '100%',
  paddingBottom: '20px',
  [theme.breakpoints.down('sm')]: {
    paddingBottom: '40px',
  }
}));

// Input de búsqueda con animaciones mejoradas
const MacOSSearchInputWrapper = styled(motion.div)(({ theme }) => ({
  position: 'relative',
  borderRadius: 20,
  backgroundColor: theme.palette.mode === 'dark' 
    ? alpha('#fff', 0.15)
    : alpha('#000', 0.06),
  width: '280px',
  height: '36px',
  transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  border: `1px solid ${theme.palette.mode === 'dark' 
    ? alpha('#fff', 0.2)
    : alpha('#000', 0.08)}`,
  display: 'flex',
  alignItems: 'center',
  '&:hover': {
    // Sin hover effects
  },
  '&:focus-within': {
    // Sin focus effects
  },
  [theme.breakpoints.down('sm')]: {
    width: '90%',
    maxWidth: '280px',
  }
}));

// Contenedor del icono y texto con animaciones
const SearchContentWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isFocused' && prop !== 'hasText',
})(({ isFocused, hasText }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  justifyContent: isFocused || hasText ? 'flex-start' : 'center',
  transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  padding: '0 12px',
}));

// Icono de búsqueda
const MacOSSearchIconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.actualMode === 'wallpaper'
    ? alpha('#fff', 0.9)
    : theme.palette.mode === 'dark'
      ? alpha('#fff', 0.9)
      : alpha('#000', 0.6),
  marginRight: '6px',
  transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
}));

// Input base
const MacOSInputBase = styled(InputBase, {
  shouldForwardProp: (prop) => prop !== 'isFocused' && prop !== 'hasText',
})(({ theme, isFocused, hasText }) => ({
  color: theme.palette.actualMode === 'wallpaper'
    ? '#fff'
    : theme.palette.mode === 'dark' ? '#fff' : '#000',
  fontSize: '0.9rem',
  fontWeight: 500,
  width: '100%',
  transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  '& .MuiInputBase-input': {
    padding: '0',
    textAlign: isFocused || hasText ? 'left' : 'center',
    transition: 'text-align 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  },
  '&::placeholder': {
    color: theme.palette.actualMode === 'wallpaper'
      ? alpha('#fff', 0.7)
      : theme.palette.mode === 'dark'
        ? alpha('#fff', 0.7)
        : alpha('#000', 0.5),
    opacity: 1,
  },
}));

// Grid mejorado
const AppGrid = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(2),
  width: '100%',
  marginTop: '20px',
  display: 'flex',
  justifyContent: 'center',
  maxWidth: '1000px',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
    marginTop: '10px',
  }
}));

// AppItem con animaciones mejoradas
const AppItem = styled(motion.div)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  cursor: 'pointer',
  padding: theme.spacing(1),
  borderRadius: 8,
  transition: 'all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.5),
  }
}));

// AppIcon SIN framer-motion para evitar glitches - usa CSS puro
// Con soporte para modo dark/light (borde gradiente + fondo sólido) y wallpaper (glassmorphism)
const AppIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'gradient' && prop !== 'gradientDark' && prop !== 'appId',
})(({ theme, gradient, gradientDark, appId }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const isDarkMode = theme.palette.mode === 'dark';

  // Selecciona el gradiente apropiado según el modo
  const baseGradient = gradient
    ? (isDarkMode && gradientDark ? gradientDark : gradient)
    : (isDarkMode
      ? 'linear-gradient(to bottom, #1C1C1E, #0F0F11)'
      : 'linear-gradient(to bottom, #1BB3F3, #186CE6)');

  // Para Chat en dark mode, extraer el color más claro para borde sólido
  const extractColors = (gradientString) => {
    const colorRegex = /#[0-9A-Fa-f]{6}/g;
    return gradientString.match(colorRegex) || [];
  };

  // Función para invertir color (negativo suavizado - gris en lugar de blanco)
  const invertColor = (hex) => {
    // Remover # si existe
    hex = hex.replace('#', '');

    // Convertir a RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Invertir cada componente pero con un máximo de 180 en lugar de 255
    // Esto produce grises suaves en lugar de blancos brillantes
    const maxValue = 180;
    const invR = Math.min(255 - r, maxValue).toString(16).padStart(2, '0');
    const invG = Math.min(255 - g, maxValue).toString(16).padStart(2, '0');
    const invB = Math.min(255 - b, maxValue).toString(16).padStart(2, '0');

    return `#${invR}${invG}${invB}`;
  };

  const isChatInDark = appId === 'chat' && isDarkMode && !isWallpaperMode;
  const chatLightColor = isChatInDark ? extractColors(gradientDark || gradient)[0] || '#415B7E' : null;

  // Para Fin en ambos modos, invertir los colores del gradiente (negativo) para diferenciarse
  const isFin = appId === 'fin' && !isWallpaperMode;
  let borderGradient = baseGradient;

  if (isFin) {
    const gradColors = extractColors(gradientDark || gradient);
    const invertedColors = gradColors.map(invertColor);
    borderGradient = `linear-gradient(to bottom, ${invertedColors.join(', ')})`;
  }

  // Extraer color para el hover shadow (color más oscuro del gradiente)
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const gradientColors = extractColors(borderGradient);
  const shadowColor = gradientColors.length > 0
    ? gradientColors[gradientColors.length - 1] // Color más oscuro del gradiente
    : '#007BFE'; // Fallback azul
  const hoverShadow = hexToRgba(shadowColor, 0.3);

  // Para iconos sin gradiente (como Home o Settings): color del icono debe coincidir con el borde
  const iconColor = !gradient && !isDarkMode ? '#007aff' : '#FFFFFF';

  return {
    position: 'relative',
    width: 64,
    height: 64,
    borderRadius: 15,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(0.5),
    // En wallpaper: fondo semi-transparente con blur
    // En dark/light: fondo sólido
    background: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.15)'
      : (isDarkMode ? '#1C1C1E' : '#FFFFFF'),
    // Padding para el borde gradiente (solo en light/dark)
    padding: isWallpaperMode ? 0 : '2px',
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(255, 255, 255, 0.2)' : 'none',
    color: iconColor,
    boxShadow: isWallpaperMode
      ? '0 2px 8px rgba(0, 0, 0, 0.1)'
      : '0 2px 8px rgba(0,0,0,0.15)',
    transition: 'transform 0.2s ease-out, box-shadow 0.2s ease-out',
    cursor: 'pointer',
    transform: 'translateZ(0)',
    backfaceVisibility: 'hidden',
    WebkitFontSmoothing: 'antialiased',
    // Pseudo-elemento para el borde con gradiente (solo en light/dark)
    // Chat en dark usa borde sólido del color más claro
    // Fin en dark usa gradiente invertido
    '&::before': isWallpaperMode ? {} : {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: 15,
      padding: '2px',
      background: isChatInDark ? chatLightColor : borderGradient,
      WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      WebkitMaskComposite: 'xor',
      maskComposite: 'exclude',
      pointerEvents: 'none',
    },
    '&:hover': {
      transform: 'translateZ(0) scale(1.05)',
      boxShadow: isWallpaperMode
        ? '0 6px 16px rgba(255, 255, 255, 0.3)'
        : `0 6px 16px ${hoverShadow}`,
    },
    '&:active': {
      transform: 'translateZ(0) scale(0.97)',
    },
    '& svg': {
      width: '70%',
      height: '70%',
      pointerEvents: 'none',
      display: 'block',
    },
    [theme.breakpoints.down('sm')]: {
      width: 48,
      height: 48,
      borderRadius: 12,
    }
  };
});

const AppName = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(0.5),
  fontSize: '0.85rem',
  fontWeight: 500,
  color: theme.palette.actualMode === 'wallpaper'
    ? '#FFFFFF'
    : theme.palette.mode === 'dark' ? '#fff' : '#000',
  textAlign: 'center',
  transition: 'all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.75rem',
    marginTop: theme.spacing(0.25),
  }
}));

// Definición de las aplicaciones
// Todas usan ícono del cerebro con gradientes específicos de cada AGT (light y dark)
const apps = [
  {
    id: 'chat',
    name: 'Chat',
    gradient: CHAT_GRADIENT_LIGHT,
    gradientDark: CHAT_GRADIENT_DARK
  },
  {
    id: 'fin',
    name: 'Fin',
    gradient: JOB_GRADIENT_LIGHT,
    gradientDark: JOB_GRADIENT_DARK
  },
  {
    id: 'fit',
    name: 'Fit',
    gradient: FIT_GRADIENT_LIGHT,
    gradientDark: FIT_GRADIENT_DARK
  },
  {
    id: 'health',
    name: 'Health',
    gradient: HEALTH_GRADIENT_LIGHT,
    gradientDark: HEALTH_GRADIENT_DARK
  },
  {
    id: 'ups',
    name: 'Ups',
    gradient: UPS_GRADIENT_LIGHT,
    gradientDark: UPS_GRADIENT_DARK
  },
  {
    id: 'store',
    name: 'Store',
    gradient: LIFEOS_GRADIENT_LIGHT,
    gradientDark: LIFEOS_GRADIENT_DARK
  },
  {
    id: 'settings',
    name: 'Settings',
    icon: Settings,
    mobileOnly: true
  },
];

// Variantes de animación - simplificadas para evitar glitches
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.2,
      ease: 'easeOut',
      staggerChildren: 0.02,
      delayChildren: 0.05
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.15,
      ease: 'easeOut'
    }
  }
};

const searchVariants = {
  hidden: { 
    opacity: 0,
    y: -20,
    scale: 0.95
  },
  visible: { 
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const appItemVariants = {
  hidden: {
    opacity: 0,
    y: 10
  },
  visible: (index) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.2,
      delay: index * 0.02,
      ease: 'easeOut'
    }
  })
};

// const iconHoverVariants = {
//   hover: {
//     scale: 1.08,
//     boxShadow: '0 8px 20px rgba(0, 123, 254, 0.3)',
//     transition: {
//       duration: 0.2,
//       ease: [0.25, 0.46, 0.45, 0.94]
//     }
//   },
//   tap: {
//     scale: 0.95,
//     transition: {
//       duration: 0.1,
//       ease: [0.25, 0.46, 0.45, 0.94]
//     }
//   }
// };

const closeButtonVariants = {
  hidden: { 
    opacity: 0,
    scale: 0.8,
    rotate: -90
  },
  visible: { 
    opacity: 1,
    scale: 1,
    rotate: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  },
  hover: {
    scale: 1.1,
    rotate: 90,
    transition: {
      duration: 0.3,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  },
  tap: {
    scale: 0.95,
    transition: {
      duration: 0.1,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const Launchpad = ({ open, onClose, onNavigate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredApps, setFilteredApps] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isVerySmallDevice = useMediaQuery('(max-width:360px)');
  
  useEffect(() => {
    if (open) {
      window.setTimeout(() => {
        window.scrollTo(0, 0);
      }, 100);
    }
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus();
        setIsFocused(true);
      }, 300);
    } else {
      setSearchTerm('');
      setIsFocused(false);
    }
  }, [open]);

  useEffect(() => {
    const deviceFilteredApps = apps.filter(app => 
      !app.mobileOnly || (app.mobileOnly && isMobile)
    );
    
    if (searchTerm.trim() === '') {
      setFilteredApps(deviceFilteredApps);
    } else {
      const searchFiltered = deviceFilteredApps.filter(app => 
        app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t(`navigation.${app.id}`, app.name).toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredApps(searchFiltered);
    }
  }, [searchTerm, t, isMobile]);

  const handleAppClick = (appId) => {
    if (appId === 'settings') {
      const event = new CustomEvent('openSettings');
      window.dispatchEvent(event);
      onClose();
      return;
    }

    if (appId === 'store') {
      onNavigate('promo');
      onClose();
      return;
    }

    const implementedApps = ['chat', 'ups', 'fin', 'edu', 'promo', 'health', 'fit'];
    if (implementedApps.includes(appId)) {
      onNavigate(appId);
      onClose();
      return;
    }

    onNavigate('promo');
    onClose();
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'Enter' && filteredApps.length > 0) {
      handleAppClick(filteredApps[0].id);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    if (searchTerm === '') {
      setIsFocused(false);
    }
  };

  const hasText = searchTerm.length > 0;

  const getGridSize = () => {
    if (isVerySmallDevice) {
      return { xs: 4, sm: 3 };
    }
    if (isMobile) {
      return { xs: 4, sm: 3 };
    }
    return { xs: 4, sm: 3, md: 2, lg: 1.7 };
  };

  const gridSize = getGridSize();
  const iconSize = isMobile ? (isVerySmallDevice ? 24 : 28) : 32;

  return (
    <AnimatePresence>
      {open && (
        <LaunchpadContainer
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={containerVariants}
          onClick={handleBackdropClick}
        >
          <ScrollableContent>
            {/* Botón de cierre con animaciones */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              width: '100%',
              position: 'absolute',
              top: isMobile ? 8 : 16,
              right: isMobile ? 8 : 16,
              zIndex: 2
            }}>
              <motion.div
                variants={closeButtonVariants}
                initial="hidden"
                animate="visible"
                whileHover="hover"
                whileTap="tap"
              >
                <IconButton
                  onClick={onClose}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    backgroundColor: (theme) => theme.palette.mode === 'dark' 
                      ? alpha('#fff', 0.1) 
                      : alpha('#000', 0.05),
                    transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    '&:hover': {
                      backgroundColor: (theme) => theme.palette.mode === 'dark' 
                        ? alpha('#fff', 0.2) 
                        : alpha('#000', 0.1),
                    }
                  }}
                >
                  <X size={isMobile ? 16 : 20} />
                </IconButton>
              </motion.div>
            </Box>
            
            {/* Input de búsqueda con animaciones */}
            <Box sx={{ 
              width: '100%', 
              display: 'flex', 
              justifyContent: 'center', 
              mt: isMobile ? 2 : 4,
              position: 'sticky',
              top: 0,
              zIndex: 1,
              backdropFilter: 'blur(10px)',
              pb: 1
            }}>
              <MacOSSearchInputWrapper
                variants={searchVariants}
                initial="hidden"
                animate="visible"
              >
                <SearchContentWrapper isFocused={isFocused} hasText={hasText}>
                  <MacOSSearchIconWrapper>
                    <Search size={16} />
                  </MacOSSearchIconWrapper>
                  
                  <MacOSInputBase
                    placeholder={t('launchpad.searchApps', 'Search')}
                    inputProps={{ 'aria-label': 'search' }}
                    inputRef={inputRef}
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onKeyDown={handleKeyDown}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    isFocused={isFocused}
                    hasText={hasText}
                    autoFocus
                  />
                </SearchContentWrapper>
              </MacOSSearchInputWrapper>
            </Box>

            {/* Grid de aplicaciones con animaciones */}
            <AppGrid 
              container 
              spacing={isMobile ? (isVerySmallDevice ? 0.5 : 1) : 1} 
              justifyContent="center"
            >
              <AnimatePresence>
                {filteredApps.map((app, index) => (
                  <Grid item {...gridSize} key={app.id}>
                    <AppItem 
                      custom={index}
                      variants={appItemVariants}
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                      onClick={() => handleAppClick(app.id)}
                    >
                      <AppIcon gradient={app.gradient} gradientDark={app.gradientDark} appId={app.id}>
                        {app.icon ? (
                          // Iconos sin gradiente (Home, Settings): heredan color del AppIcon
                          React.createElement(app.icon, { size: iconSize })
                        ) : app.id === 'store' ? (
                          <BrainStoreIconWithGradient />
                        ) : (
                          <BrainIconWithGradient
                            gradient={app.gradient}
                            gradientDark={app.gradientDark}
                            appId={app.id}
                          />
                        )}
                      </AppIcon>
                      <AppName>
                        {app.id === 'support' 
                          ? t('homeInput.cards.support')
                          : app.id === 'chat'
                            ? t('homeInput.cards.chat')
                            : t(`navigation.${app.id}`, app.name)}
                      </AppName>
                    </AppItem>
                  </Grid>
                ))}
              </AnimatePresence>
            </AppGrid>
          </ScrollableContent>
        </LaunchpadContainer>
      )}
    </AnimatePresence>
  );
};

export default Launchpad;